import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const VISITOR_KEY = "visitor_id";
const SESSION_KEY = "visitor_session_id";
const SESSION_TTL_MIN = 30;

const getVisitorId = (): string => {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = `v_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
};

const getSessionId = (): string => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const { id, ts } = JSON.parse(raw);
      if (Date.now() - ts < SESSION_TTL_MIN * 60 * 1000) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
        return id;
      }
    }
  } catch {}
  const id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
  return id;
};

// Track a discrete event (click, action, etc.) — call from anywhere via window event
export const trackEvent = async (
  eventName: string,
  metadata?: Record<string, unknown>,
) => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    await supabase.from("visitor_analytics").insert({
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      user_id: auth?.user?.id ?? null,
      page_path: window.location.pathname,
      event_type: "event",
      event_name: eventName,
      metadata: (metadata ?? null) as never,
      user_agent: navigator.userAgent,
      referrer: document.referrer || null,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
    });
  } catch (err) {
    console.error("trackEvent failed", err);
  }
};

export function useVisitorTracking() {
  const location = useLocation();
  const currentRowId = useRef<string | null>(null);
  const pageEnteredAt = useRef<number>(Date.now());

  // Flush time spent on the current page row
  const flushDuration = async () => {
    const id = currentRowId.current;
    if (!id) return;
    const seconds = Math.max(0, Math.round((Date.now() - pageEnteredAt.current) / 1000));
    if (seconds === 0) return;
    try {
      await supabase.rpc("bump_visit_duration", { _id: id, _seconds: seconds });
    } catch {}
  };


  useEffect(() => {
    let cancelled = false;
    const insertPageView = async () => {
      // Flush previous page's duration before starting a new one
      await flushDuration();

      const { data: auth } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("visitor_analytics")
        .insert({
          visitor_id: getVisitorId(),
          session_id: getSessionId(),
          user_id: auth?.user?.id ?? null,
          page_path: location.pathname,
          event_type: "page_view",
          user_agent: navigator.userAgent,
          referrer: document.referrer || null,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
        })
        .select("id")
        .single();

      if (!cancelled && !error && data) {
        currentRowId.current = data.id;
        pageEnteredAt.current = Date.now();
      }
    };

    insertPageView();

    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushDuration();
    };
    const onBeforeUnload = () => flushDuration();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onBeforeUnload);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onBeforeUnload);
      flushDuration();
    };
  }, [location.pathname]);
}
